import * as Sentry from "@sentry/react";
import { redirect, createBrowserRouter as rrCreateBrowserRouter, Outlet } from "react-router-dom";

import ErrorBoundary from "../ErrorBoundary";

import { isAuthenticated } from "./components/api/auth";
import App from "./components/cm/App";
import { invalidateSession } from "./components/ui library/helpers/functions";
import { createDynamicViewRoutes } from "./dynamicViewRoutes";
import { isPublicRoute } from "./publicRoutes";

const createBrowserRouter = Sentry.wrapCreateBrowserRouter(rrCreateBrowserRouter);

const podModules = require.context("./pods", true, /route\.js$/);

const hasActiveSession = () => isAuthenticated();

const withSessionRedirects = (loader, { isLoginRoute, isPublic }) => {
  return async (args) => {
    const currentUrl = new URL(args.request.url);
    const hasAuthToken = currentUrl.searchParams.has("auth_token");
    const activeSession = hasActiveSession();

    // Invalidate session if auth_token is present in URL and user is not on login route
    if (hasAuthToken && !isLoginRoute) {
      if (activeSession) {
        await invalidateSession({ skipRedirect: true });
      }
      return redirect(`/?${currentUrl.searchParams.toString()}`);
    }

    if (activeSession && isLoginRoute && !hasAuthToken) {
      return redirect("/landing");
    }

    if (!activeSession && !isLoginRoute && !isPublic) {
      await invalidateSession();
      return redirect("/");
    }

    if (typeof loader === "function") {
      return loader(args);
    }

    return null;
  };
};

const groupRoutesByHierarchy = () => {
  const routes = [];

  podModules.keys().forEach((key) => {
    // Remove the './pods' part and normalize the path.
    const pathParts = key
      .replace("./pods/", "") // Remove the base folder
      .replace(/\/route\.js$/, "") // Remove the file name
      .split("/"); // Split by directory slashes

    let currentLevel = routes;

    pathParts.forEach((part, index) => {
      // Filter out any empty strings or '.' routes
      if (part === "." || part === "") {
        return;
      }

      let existingPath = currentLevel.find((route) => route.path === part);

      if (!existingPath) {
        // If the route doesn't exist, create it
        existingPath = { path: part, children: [] };
        currentLevel.push(existingPath);
      }

      // If we're at the last part, add the module to it
      if (index === pathParts.length - 1) {
        existingPath.module = podModules(key).default; // Add the module here
      }

      // Move to the next level of children
      currentLevel = existingPath.children;
    });
  });

  return routes;
};

const hierarchicalRoutes = groupRoutesByHierarchy();

const createRoute = (parentPath, route) => {
  const fullPath = parentPath ? `${parentPath}/${route.path}` : route.path;

  let routeInstance;
  const isLoginRoute = fullPath === "login";
  const isPublic = isPublicRoute(fullPath);

  if (route.module) {
    if (route.module.prototype?.constructor?.isRouteClass) {
      routeInstance = route.module.getInstance(route.module);
      routeInstance.path = routeInstance.path ?? route.path;
      routeInstance.children = route.children;
      routeInstance.loader = routeInstance.loader.bind(routeInstance);
      if (typeof routeInstance.shouldRevalidate === "function") {
        routeInstance.shouldRevalidate = routeInstance.shouldRevalidate.bind(routeInstance);
      }
    } else {
      routeInstance = route.module;
      routeInstance.path = routeInstance.path ?? route.path;
      routeInstance.children = route.children;
    }
  } else {
    routeInstance = route;
  }

  // create a stable id if not defined in the module
  const routeId =
    routeInstance.id ??
    // e.g. "clients/:id" -> "clients__param_id"
    fullPath.replace(/^\/+/, "").replace(/\/+/g, "__").replace(/:/g, "param_");

  const guardedLoader = withSessionRedirects(routeInstance.loader, { isLoginRoute, isPublic });

  if (routeInstance.index === true) {
    return {
      path: routeInstance.path,
      element: routeInstance.element,
      index: true,
      loader: guardedLoader,
    };
  } else {
    return {
      id: routeId,
      path: routeInstance.path,
      element: routeInstance.element ?? <Outlet />,
      children: routeInstance.children.map((child) => {
        return createRoute(fullPath, child);
      }),
      loader: guardedLoader,
      shouldRevalidate: routeInstance.shouldRevalidate,
    };
  }
};

const indexRoutes = [];
const processedRoutes = hierarchicalRoutes.reduce((acc, route) => {
  const res = createRoute("", route);
  let rtn = acc;
  if (res) {
    if (res.index) {
      indexRoutes.push(Object.fromEntries(Object.entries(res).filter(([key]) => key !== "path")));
    } else {
      rtn = [...rtn, res];
    }
  }
  return rtn;
}, []);

const routes = [];

if (indexRoutes.length > 1) {
  throw new Error(`It looks like you identified more than one route as the 'index' route`);
} else if (indexRoutes.length === 1) {
  routes.push(indexRoutes[0]);
}

routes.push({
  path: "/",
  element: <App />,
  children: [
    ...createDynamicViewRoutes(withSessionRedirects),
    ...processedRoutes,
    {
      path: "*",
      loader: () => redirect("/not-found"),
    },
  ],
  errorElement: <ErrorBoundary />,
});

const router = createBrowserRouter(routes);

export default router;
